/**
 * Shared time-range vocabulary for the admin dashboard.
 *
 * ONE convention, everywhere: **local calendar days**. A range is a span of whole
 * days in the operator's own timezone — `today` is midnight-to-midnight where the
 * operator is sitting, and `30d` is the last 30 whole days, today included: it
 * starts 29 days back, not 30. One pill means one window, so `resolveRange()`,
 * `rangeDays()` and `describeRange()` all report the same N. Nothing here
 * is UTC-based, because "how many books sold today" is a question about the
 * operator's calendar, not Greenwich's.
 *
 * Custom ranges are carried as `YYYY-MM-DD` **strings**, not `Date` objects. That is
 * deliberate: a `Date` round-tripped through a URL either loses its timezone or
 * silently shifts a day (`new Date('2026-08-03')` is UTC midnight, which is Aug 2 in
 * every American timezone). Strings round-trip losslessly; they are turned into local
 * `Date` boundaries only at the moment a caller asks for them.
 *
 * This module is pure — no React, no fetch, no MUI. The pill control lives in
 * `TimeRangeFilter.tsx`, URL persistence in `useTimeRangeParam.ts`.
 *
 * **Wire formats are NOT unified here.** Each backend already speaks its own dialect
 * (`range=30d`, `period=30d`, `days=30`, UTC `from`/`to` dates, local ISO instants,
 * local `YYYY-MM-DD`). Per-screen adapters translate at the call site using
 * `toApiToken()`, `rangeDays()`, `resolveRange()` or `resolveRangeYmd()`. Do not
 * "helpfully" rewrite a backend to match a different dialect.
 */

import {
  differenceInCalendarDays,
  endOfDay,
  format,
  isValid,
  startOfDay,
  subDays,
} from 'date-fns';

/** The canonical six. Business/analytics screens use exactly these. */
export type TimeRangeToken = 'today' | '3d' | '7d' | '14d' | '30d' | '90d';

/**
 * A resolved selection: either a preset token or an explicit custom span.
 *
 * Generic on the token so monitoring screens (hour scale) and cost screens (QTD/YTD)
 * can render the same control over their own vocabulary: `TimeRangeValue<HourToken>`.
 * Default is the canonical six.
 */
export type TimeRangeValue<T extends string = TimeRangeToken> =
  | { type: T; from?: undefined; to?: undefined }
  | { type: 'custom'; from: string; to: string };

/** One pill. `token` is what lands in the URL and (usually) on the wire. */
export interface TimeRangePreset {
  token: string;
  label: string;
}

/** The spec set — Today · 3D · 7D · 14D · 30D · 90D. */
export const DAY_PRESETS: TimeRangePreset[] = [
  { token: 'today', label: 'Today' },
  { token: '3d', label: '3D' },
  { token: '7d', label: '7D' },
  { token: '14d', label: '14D' },
  { token: '30d', label: '30D' },
  { token: '90d', label: '90D' },
];

/** Tokens the monitoring screens speak. */
export type HourToken = '1h' | '6h' | '24h' | '7d' | '30d';

/** Tokens the CloudWatch log viewer speaks. */
export type LogToken = '10m' | '30m' | '1h' | '6h' | '24h' | '7d';

/**
 * Hour-scale set for the monitoring screens (ECS, API health, website monitor).
 * Those screens resolve their own sub-day tokens — `resolveRange()` below only
 * speaks calendar days.
 */
export const HOUR_PRESETS: TimeRangePreset[] = [
  { token: '1h', label: '1H' },
  { token: '6h', label: '6H' },
  { token: '24h', label: '24H' },
  { token: '7d', label: '7D' },
  { token: '30d', label: '30D' },
];

/**
 * Log-window set for CloudWatchLogsViewer, which reasons in minutes.
 * Same caveat as HOUR_PRESETS: the screen resolves its own tokens.
 */
export const LOG_PRESETS: TimeRangePreset[] = [
  { token: '10m', label: '10m' },
  { token: '30m', label: '30m' },
  { token: '1h', label: '1h' },
  { token: '6h', label: '6h' },
  { token: '24h', label: '24h' },
  { token: '7d', label: '7d' },
];

/** Day counts behind the canonical tokens. `today` is absent on purpose — it is 0 days back. */
const DAYS_BACK: Record<string, number> = {
  '3d': 3,
  '7d': 7,
  '14d': 14,
  '30d': 30,
  '90d': 90,
};

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses `YYYY-MM-DD` as LOCAL midnight. `new Date(s)` would parse it as UTC and
 * shift the day backwards west of Greenwich — the bug this whole module exists to
 * avoid. Returns null for anything that is not a real calendar date (2026-02-31).
 */
function parseYmdLocal(value: string | null | undefined): Date | null {
  if (!value || !YMD.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (!isValid(date)) return null;
  // Rejects overflow dates: new Date(2026, 1, 31) silently becomes March 3.
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

/** True when `value` is a real local calendar date in `YYYY-MM-DD` form. */
export function isValidYmd(value: string | null | undefined): boolean {
  return parseYmdLocal(value) !== null;
}

/** Formats a Date as a local `YYYY-MM-DD` string. */
export function toYmd(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Concrete inclusive bounds for a range, in local time.
 *
 * `today`  → [startOfDay(now), endOfDay(now)]
 * `Nd`     → [startOfDay(now − (N−1) days), endOfDay(now)] — exactly N calendar
 *            days with today as the last of them, so it agrees with `rangeDays()`.
 * `custom` → [startOfDay(from), endOfDay(to)]
 *
 * Unknown tokens fall back to 90 days — the same defensive default the per-chart
 * range selector this replaces used.
 */
export function resolveRange(value: TimeRangeValue<string>): { from: Date; to: Date } {
  const now = new Date();

  if (value.type === 'custom') {
    const from = parseYmdLocal(value.from);
    const to = parseYmdLocal(value.to);
    if (from && to) return { from: startOfDay(from), to: endOfDay(to) };
    // 89 back plus today = 90 whole days, the same span `rangeDays()` reports.
    return { from: startOfDay(subDays(now, 89)), to: endOfDay(now) };
  }

  if (value.type === 'today') return { from: startOfDay(now), to: endOfDay(now) };

  // N − 1 days back, because today is the Nth day of the window.
  return { from: startOfDay(subDays(now, (DAYS_BACK[value.type] ?? 90) - 1)), to: endOfDay(now) };
}

/** The same bounds as `resolveRange`, as local `YYYY-MM-DD` strings. */
export function resolveRangeYmd(value: TimeRangeValue<string>): { from: string; to: string } {
  const { from, to } = resolveRange(value);
  return { from: toYmd(from), to: toYmd(to) };
}

/**
 * INCLUSIVE day count — what `days=` style endpoints want.
 * `today` is 1 day, `30d` is 30, a custom Aug 3 → Aug 21 is 19.
 */
export function rangeDays(value: TimeRangeValue<string>): number {
  if (value.type === 'custom') {
    const from = parseYmdLocal(value.from);
    const to = parseYmdLocal(value.to);
    if (from && to) return Math.max(1, differenceInCalendarDays(to, from) + 1);
    return 90;
  }
  if (value.type === 'today') return 1;
  return DAYS_BACK[value.type] ?? 90;
}

/** The token to put on the wire: the preset token, or `'custom'`. */
export function toApiToken(value: TimeRangeValue<string>): string {
  return value.type;
}

/** Sentence fragment for card captions: "today", "the last 30 days", a custom span. */
export function describeRange(value: TimeRangeValue<string>): string {
  if (value.type === 'custom') {
    const from = parseYmdLocal(value.from);
    const to = parseYmdLocal(value.to);
    if (from && to) return `${format(from, 'd MMM yyyy')} – ${format(to, 'd MMM yyyy')}`;
    return 'the last 90 days';
  }
  if (value.type === 'today') return 'today';
  return `the last ${DAYS_BACK[value.type] ?? 90} days`;
}

/**
 * Label for the Custom pill once a span is applied.
 * Same year → "Aug 3 – Aug 21"; crossing a year boundary → "Dec 28, 2025 – Jan 4, 2026".
 */
export function formatCustomLabel(value: { from: string; to: string }): string {
  const from = parseYmdLocal(value.from);
  const to = parseYmdLocal(value.to);
  if (!from || !to) return 'Custom';
  const sameYear = from.getFullYear() === to.getFullYear();
  const pattern = sameYear ? 'MMM d' : 'MMM d, yyyy';
  return `${format(from, pattern)} – ${format(to, pattern)}`;
}

/** True when `token` is one of the given presets (defaults to the canonical six). */
export function isPresetToken(
  token: string | null | undefined,
  presets: TimeRangePreset[] = DAY_PRESETS
): boolean {
  if (!token) return false;
  return presets.some((p) => p.token === token);
}

/**
 * Turns URL params into a value, falling back whenever they do not describe a real
 * range. Custom needs BOTH dates, both well-formed, and from ≤ to — a half-filled or
 * inverted custom range is a typo in the address bar, not a selection.
 */
export function parseRangeParams<T extends string = TimeRangeToken>(
  range: string | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined,
  fallback: TimeRangeValue<T>,
  presets: TimeRangePreset[] = DAY_PRESETS
): TimeRangeValue<T> {
  if (!range) return fallback;

  if (range === 'custom') {
    const parsedFrom = parseYmdLocal(from);
    const parsedTo = parseYmdLocal(to);
    if (!parsedFrom || !parsedTo || parsedFrom > parsedTo) return fallback;
    return { type: 'custom', from: from as string, to: to as string };
  }

  if (isPresetToken(range, presets)) return { type: range as T };

  return fallback;
}
